/**
 * Блок вкладки «Голоса клиента» (#848): заголовок с N, строки-карточки с
 * раскрытием до разговоров. N = 0 — «не собрано» словами, а не строки
 * «0 из 0» (#1379); собрано, но пусто — своя строка блока.
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardPanel } from '@/components/dashboard/dashboard-panel';
import { VoiceRowCard } from './VoiceRowCard';
import { VoiceSeriesNote } from './VoiceSeriesNote';
import { VoiceTopList } from './VoiceTopList';
import { useVoiceBlockSeries } from './useVoiceSeries';
import type { VoiceRowDetailsTrend } from './voice-series-view-model';
import { rowSeriesRef } from './voice-series-view-model';
import {
  sectionEmptyKey,
  sectionZeroKey,
  type VoiceRow,
  type VoiceSection,
} from './voice-view-model';

interface VoiceSectionPanelProps {
  section: VoiceSection;
  /** N of the block — what «0» means is decided by it. */
  callsInWindow: number;
  /** The header's N, in the block's own words (conversations, calls, declines). */
  windowLabel: string;
  /** The expanded row (`kind:key`) from the shell's address. */
  expandedRow: string | undefined;
  onToggleRow: (rowId: string) => void;
  onOpenCall: (recordId: string) => void;
  /** Latest wordings seen on a collapsed row. */
  preview?: number;
  /** A line under the title: what the block counts. */
  hint?: string;
  /**
   * Above the rows and shown whatever N is: the themes block's coverage and
   * «no theme» — given each `headerRows` row's series (#2394 §9 п. 7).
   */
  header?: (trendOf: (row: VoiceRow) => VoiceRowDetailsTrend | undefined) => ReactNode;
  /** Rows the header draws: asked with the block's series. */
  headerRows?: readonly VoiceRow[];
  /** Notes under the rows. */
  children?: ReactNode;
  /** The first rows only, then «Show all N» (#2060): the layered tabs pass it. */
  limit?: number;
  /** Rows per page, with paging (#2314): Marketing and Product and service pass it. */
  pageSize?: number;
  /** A row marked as current — the theme filter's theme in the themes block (#2229). */
  highlightedRow?: string;
  /** The rows' counts are a capped scan's lower bound (`VoiceSeriesNote`, #2596). */
  lowerBound?: boolean;
}

export function VoiceSectionPanel({
  section,
  callsInWindow,
  windowLabel,
  expandedRow,
  onToggleRow,
  onOpenCall,
  preview,
  hint,
  header,
  headerRows = [],
  children,
  limit,
  pageSize,
  highlightedRow,
  lowerBound,
}: VoiceSectionPanelProps) {
  const { t } = useTranslation('dashboard');
  // The block's series (#2394 §9 п. 7): one request per signal, its own — the
  // rows do not wait for it. Only of the rows on screen: those folded under
  // «Show all» or on another page are asked when they are shown.
  const [shownKeys, setShownKeys] = useState<readonly string[]>([]);
  const series = useVoiceBlockSeries(
    callsInWindow === 0
      ? []
      : [...headerRows, ...section.rows.filter((row) => shownKeys.includes(row.id))],
    (row: VoiceRow) => ({
      ref: rowSeriesRef(row),
      calls: row.calls,
      prevCalls: row.prevCalls,
    })
  );
  return (
    <DashboardPanel
      title={t(`voicePage.sections.${section.id}`)}
      action={<span className="text-xs text-fg-muted">{windowLabel}</span>}
    >
      {hint && <p className="mb-3 text-xs text-fg-muted">{hint}</p>}
      {header?.(series.trendOf)}
      {callsInWindow === 0 ? (
        <p className="py-6 text-center text-sm text-fg-muted">
          {t(sectionZeroKey(section))}
        </p>
      ) : section.rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-fg-muted">
          {t(sectionEmptyKey(section))}
        </p>
      ) : (
        <>
          <VoiceSeriesNote block={series.block} lowerBound={lowerBound} />
          <VoiceTopList
            rows={section.rows}
            limit={limit}
            pageSize={pageSize}
            label={t(`voicePage.sections.${section.id}`)}
            rowKey={(row) => row.id}
            calls={(row) => row.calls}
            revealKeys={[expandedRow, highlightedRow]}
            onShownKeys={setShownKeys}
            renderRow={(row) => (
              <VoiceRowCard
                key={row.id}
                row={row}
                expanded={expandedRow === row.id}
                onToggle={() => onToggleRow(row.id)}
                onOpenCall={onOpenCall}
                preview={preview}
                highlighted={highlightedRow === row.id}
                trend={series.trendOf(row)}
              />
            )}
          />
        </>
      )}
      {children}
    </DashboardPanel>
  );
}
